import {buildIconDataUri} from '../../../../components/sceneEditor/calloutRender';
import {CALLOUT_TYPES} from '../../../../components/sceneEditor/calloutTypes';

/**
 * Rewrite the markdown-it callout HTML in an import-PREVIEW part into the same
 * `.callout` structure the reader/editor render, so the preview shows the same
 * boxes + icons.
 *
 * Two problems this solves:
 *  1. The shared sanitizer (sanitizeHtmlUtil) does not allow `<aside>` and
 *     drops `data-*` attributes, so the raw markdown-it `<aside
 *     data-callout-type data-icon>` collapses to bare paragraphs in the
 *     preview. We emit `<div class="callout callout--TYPE">` instead — `div`
 *     and `class` survive sanitising.
 *  2. The glyph is drawn by the tiptap node's renderHTML, which the preview
 *     never runs. We inline the same `<img class="callout__glyph">` data-URI
 *     SVG here (from the reader's buildIconDataUri), so the icon shows.
 *
 * IMPORTANT: this operates on a COPY of the HTML for display only — it does not
 * touch `part.html`, which still feeds the real import (markdownify → tiptapify
 * → generateJSON reads `data-callout-type`/`data-icon` off the ORIGINAL
 * `<aside>`). So the tiptap node keeps drawing its own single glyph; there is
 * no double-rendered icon in the imported book.
 *
 * DOMParser('text/html') neither executes scripts nor loads resources, and the
 * result is still passed through sanitizeHtml at the call site, so parsing the
 * (untrusted) part HTML here is safe.
 */
export function transformCalloutsForPreview(html) {
  if (!html || html.indexOf('data-callout-type') === -1) return html || '';

  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('aside[data-callout-type]').forEach((aside) => {
    const rawType = aside.getAttribute('data-callout-type') || 'aside';
    const type = CALLOUT_TYPES.includes(rawType) ? rawType : 'aside';
    const icon = aside.getAttribute('data-icon') || null;

    const box = doc.createElement('div');
    box.className = `callout callout--${type}`;

    const glyph = doc.createElement('img');
    glyph.className = 'callout__glyph';
    glyph.setAttribute('src', buildIconDataUri(icon, type));
    glyph.setAttribute('alt', '');

    const body = doc.createElement('div');
    body.className = 'callout__body';
    while (aside.firstChild) body.appendChild(aside.firstChild);

    box.appendChild(glyph);
    box.appendChild(body);
    aside.replaceWith(box);
  });

  return doc.body.innerHTML;
}

export default transformCalloutsForPreview;
